// =============================================================================
// Validation & Rate App
// =============================================================================

export const VALIDATION = {
  /**
   * Minimum username length. Auto-generated slugs are
   * `USERNAME_GENERATED_LENGTH` chars; the lower floor exists to
   * provision for future user-chosen vanity names without a second
   * migration.
   */
  USERNAME_MIN_LENGTH: 4,
  /** Maximum username length (provisions for future user-chosen vanity names) */
  USERNAME_MAX_LENGTH: 64,
  /**
   * Length of auto-generated usernames (Crockford base32 chars). Tunable
   * — increasing it raises the keyspace and reduces collision retry
   * pressure as the user base grows. Must satisfy
   * `USERNAME_MIN_LENGTH <= USERNAME_GENERATED_LENGTH <= USERNAME_MAX_LENGTH`.
   *
   * Keyspace at length L: 32^L
   *   L=8  → ~1.1 × 10^12   (50% collision at ~1M users)
   *   L=10 → ~1.1 × 10^15   (50% collision at ~33M users)
   *   L=12 → ~1.1 × 10^18   (50% collision at ~1B users)
   */
  USERNAME_GENERATED_LENGTH: 8,
  /** Minimum display name length */
  DISPLAY_NAME_MIN_LENGTH: 3,
  /** Maximum display name length (kept short to fit mobile UI — VS screens, leaderboard rows) */
  DISPLAY_NAME_MAX_LENGTH: 20,
  /**
   * Username rename policy (#367).
   *
   * Three knobs:
   *  - `USERNAME_RENAME_COOLDOWN_DAYS`: per-user gap between renames. Stops
   *    a user from cycling identities to evade reports / ban evasion within
   *    a single news cycle. 30 days picks the same cadence as Discord's
   *    pomelo migration; long enough to make rename-as-evasion impractical
   *    without being so long it punishes legitimate single-typo recovery.
   *  - `USERNAME_RESERVATION_COOLDOWN_DAYS`: post-release reservation. When
   *    Alice renames `alice` → `alice2`, the original `alice` is parked for
   *    90 days so an attacker cannot wait, claim it, and impersonate her.
   *    The reservation entry has a TTL on `cooldownUntil` so MongoDB
   *    auto-purges expired rows.
   *  - `USERNAME_HISTORY_MAX_ENTRIES`: trim cap for the per-user audit array
   *    on the User doc. The `usernameReservations` collection is the long-
   *    term audit; the inline history is just for the rename-screen "you
   *    used to be X" cue.
   */
  USERNAME_RENAME_COOLDOWN_DAYS: 30,
  USERNAME_RESERVATION_COOLDOWN_DAYS: 90,
  USERNAME_HISTORY_MAX_ENTRIES: 10,
} as const;

/**
 * Canonical username character set + length validator.
 *
 * Uses Crockford base32: digits + lowercase letters minus look-alikes
 * (`i`, `l`, `o`, `u`). Drops `0/O`, `1/I/l` confusion for support readback
 * and avoids common offensive substrings that include `u`.
 *
 * Server schema, generator, and any user-facing input MUST validate against
 * this regex so the rules stay in sync.
 */
export const USERNAME_REGEX = /^[0-9a-hjkmnp-tv-z]{4,64}$/;

/** Names that impersonate system roles — case-insensitive exact match */
export const RESERVED_DISPLAY_NAMES: readonly string[] = [
  'admin', 'mod', 'moderator',
  'system', 'support', 'staff', 'official',
  'word clash', 'wordclash',
  'bot', 'player',
] as const;

/**
 * Blocked substrings for display names — case-insensitive partial match.
 * Covers English and Turkish profanity/slurs. Expand as needed.
 */
export const BLOCKED_DISPLAY_NAME_PATTERNS: readonly string[] = [
  // English
  'fuck', 'shit', 'bitch', 'asshole', 'bastard', 'dick', 'cock', 'cunt',
  'nigger', 'nigga', 'faggot', 'retard', 'slut', 'whore', 'pussy',
  // Turkish
  'amk', 'orospu', 'piç', 'siktir', 'yarrak', 'götveren', 'ibne',
  'pezevenk', 'kahpe', 'gavat',
] as const;

export const RATE_APP = {
  /** Show rate popup after N match wins */
  SHOW_AFTER_WINS: 5,
  /** Don't show again for N days after dismissal */
  COOLDOWN_DAYS: 30,
} as const;

/**
 * In-app support message form (Settings → Contact Us).
 * Messages land in MongoDB as `SupportMessage` rows and are surfaced to
 * staff in the admin SPA. They are NOT emailed — the support workflow is
 * entirely in-product.
 */
export const SUPPORT = {
  /** Maximum subject length — fits a single mobile row + admin table cell */
  SUBJECT_MIN_LENGTH: 3,
  SUBJECT_MAX_LENGTH: 120,
  /** Maximum body length — generous for a free-form complaint or feature ask */
  BODY_MIN_LENGTH: 10,
  BODY_MAX_LENGTH: 4000,
  /**
   * Per-user rate limit on support submissions: 5 per hour. Service-level
   * Redis Lua quota in `supportService.create`. Layered with a 10/min/IP
   * express-rate-limit at the route level to defeat account-rotation.
   */
  RATE_LIMIT_PER_HOUR: 5,
  /** Maximum length of an admin's internal note on a support message */
  ADMIN_NOTE_MAX_LENGTH: 1000,
} as const;

/**
 * Device attribution (#device-tracking).
 *
 * One `UserDevice` row per `(userId, deviceId)` records WHICH device an
 * account signs in and plays from. Every field below the device id is
 * OPTIONAL on the wire: shipped binaries send no `X-Device-*` headers and
 * must keep working unchanged, so a row from an old client carries
 * `platform: 'unknown'` rather than being rejected or dropped.
 */
export const DEVICES = {
  /**
   * A row is deleted this long after its LAST sighting (TTL on
   * `expiresAt`, refreshed on every touch). Retention is the privacy
   * control for `lastIp` — the field exists for ban-evasion and
   * multi-accounting investigation, which is a recent-history question.
   */
  RETENTION_DAYS: 180,
  /**
   * Minimum gap between two `lastSeenAt` writes for one USER on the gameplay
   * (socket) path, enforced by a Redis `SET NX` claim. A socket reconnects
   * freely — `reconnectionAttempts` is Infinity for phone-call recovery — so
   * an unthrottled touch would be a Mongo write per reconnect.
   *
   * Keyed per USER, not per (user, device): the device id is client-supplied
   * and rotatable, so a per-device key has unbounded cardinality. See
   * `userDeviceService.touchDeviceSeen`.
   */
  TOUCH_THROTTLE_SECONDS: 3600,
  /** Hard caps on client-supplied strings before they reach storage. */
  DEVICE_ID_MAX_LENGTH: 128,
  OS_VERSION_MAX_LENGTH: 32,
  /** Bound on `lastAuthProvider`. Enforced in the service, NOT by the schema
   * — every write is an upsert without `runValidators`, so `maxlength` never
   * runs. Shared so the two sites cannot silently diverge. */
  AUTH_PROVIDER_MAX_LENGTH: 32,
  APP_VERSION_MAX_LENGTH: 32,
  /** Upper bound on rows returned for one account's device list. */
  MAX_DEVICES_LISTED: 20,
  /**
   * Upper bound on ACCOUNTS returned for one device — a different quantity
   * from the line above, which is devices per account. Reusing one constant
   * for both made the two admin screens print contradictory numbers for the
   * ban-evasion view: a device with 50 accounts showed "50 accounts" on the
   * detail page and 20 rows, silently truncated, on the list it linked to.
   */
  MAX_ACCOUNTS_PER_DEVICE_LISTED: 50,
  /**
   * Hard cap on stored devices per account; the least-recently-seen row is
   * evicted past it.
   *
   * The device id is CLIENT-SUPPLIED, so without a cap an authenticated
   * attacker rotating `X-Device-Id` mints one row per request. At the
   * global 100/min API limit that is ~144k rows a day per account, retained
   * for `RETENTION_DAYS` — a storage-exhaustion vector, not a theoretical
   * one. Real accounts never approach the cap (a device inserts once, then
   * only ever updates), so eviction is unreachable in normal use.
   */
  MAX_PER_USER: 20,
} as const;
